import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { LEGAL, type LegalKey } from '../legal/content';
import { useLocale } from '../i18n';
import { homePath } from '../seo/routes';

const EMAIL = 'hello@rokas.org';

function renderParagraph(text: string): ReactNode {
  if (!text.includes(EMAIL)) return text;
  const [before, after] = text.split(EMAIL);
  return (
    <>
      {before}
      <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a>
      {after}
    </>
  );
}

export const Legal = ({ doc }: { doc: LegalKey }) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const content = LEGAL[doc][locale];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Link to={homePath(locale)} viewTransition className="eyebrow mb-8 inline-flex items-center gap-2 transition-colors hover:text-primary">
          <span aria-hidden="true">←</span> {t('legal.back')}
        </Link>

        <h1 className="font-display text-4xl font-extrabold normal-case tracking-normal md:text-5xl">
          {content.title}
        </h1>
        <p className="eyebrow mt-3 text-muted-foreground">
          {t('legal.updated')}: {content.updated}
        </p>

        <div className="mt-12 flex flex-col gap-9">
          {content.sections.map((section, i) => (
            <section key={i}>
              {section.heading ? (
                <h2 className="mb-3 font-display text-xl font-bold normal-case tracking-normal md:text-2xl">
                  {section.heading}
                </h2>
              ) : null}
              <div className="flex flex-col gap-3">
                {section.paragraphs.map((p, j) => (
                  <p key={j} className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'var(--font-sans)' }}>
                    {renderParagraph(p)}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Legal;
